import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import { useImmer } from "use-immer";
import { Icon } from "@iconify/react";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import { Dialog, IconButton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddEditWhatsappTemplate from "./AddEditWhatsappTemplate";
import "./WhatsappTemplateDialog.css";
import "../../Components/Components.css";
import mapWATemplate from "../../Helper/mapWATemplate";

import {
  createWATemplate,
  editWATemplate,
  getWATemplateList,
} from "../../Store/Actions/WABAAction";

const WhatsappTemplateDialog = ({
  isOpen,
  setIsWhatsappTemplateDialogShown,
}) => {
  const [action, setAction] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  const initialTemplateDetail = {
    name: "",
    language: "",
    category: "",
    WABAAccountID: "",
    isCallPermission: false,
    parameterFormat: "positional",
  };

  const [templateDetail, setTemplateDetail] = useState(initialTemplateDetail);
  const [templateList, setTemplateList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const initialComponentsValues = {
    header: {
      mediaType: "none",

      attachment: {
        name: null,
        type: null,
        size: null,
        file: null,
      },

      headerText: null,
      headerVariable: null,
      headerVariableName: null,
    },

    bodyText: "Sample Body Text",
    bodyVariable1: null,
    bodyVariableName1: null,
    bodyVariable2: null,
    bodyVariableName2: null,
    bodyVariable3: null,
    bodyVariableName3: null,
    bodyVariable4: null,
    bodyVariableName4: null,
    bodyVariable5: null,
    bodyVariableName5: null,
    bodyVariable6: null,
    bodyVariableName6: null,
    bodyVariable7: null,
    bodyVariableName7: null,
    bodyVariable8: null,
    bodyVariableName8: null,
    bodyVariable9: null,
    bodyVariableName9: null,
    bodyVariable10: null,
    bodyVariableName10: null,
    footerText: "",
    button: [],
  };

  // useImmer used for easier state update
  const [componentsValues, updateComponentsValues] = useImmer(
    initialComponentsValues
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchTemplateList(searchValue);
  }, [isOpen, action]);

  const columns = [
    {
      field: "WABAAccountName",
      flex: 1,
      headerName: "WABA Account",
    },

    {
      field: "TemplateName",
      flex: 1,
      headerName: "Template Name",
    },

    {
      field: "Language",
      flex: 0.8,
      headerName: "Language",

      renderCell: (params) => (
        <p>{params.row.Language === "id" ? "Indonesian" : "English"}</p>
      ),
    },

    {
      field: "Category",
      flex: 0.8,
      headerName: "Category",
    },

    {
      field: "Status",
      flex: 0.8,
      headerName: "Status",

      renderCell: (params) => (
        <p
          style={{
            color:
              params.row.Status === "REJECTED"
                ? "#FF0000"
                : params.row.Status === "APPROVED"
                ? "#0343C7"
                : "#393939",
          }}
        >
          {params.row.Status}
        </p>
      ),
    },

    {
      field: "StatusTime",
      flex: 1,
      headerName: "Status Time",

      renderCell: (params) =>
        moment.utc(params.row.StatusTime).format("DD-MMM-yyyy HH:mm:ss"),
    },

    {
      field: "Sent",
      flex: 0.4,
      headerName: "Sent",
    },

    {
      field: "Delivered",
      flex: 0.4,
      headerName: "Delivered",
    },

    {
      field: "Read",
      flex: 0.4,
      headerName: "Read",
    },

    {
      field: "Failed",
      flex: 0.4,
      headerName: "Failed",
    },

    {
      flex: 0.5,
      headerName: "Action",

      renderCell: (params) => {
        return (
          <div className="action-buttons">
            <IconButton
              onClick={() => {
                setTemplateDetail({
                  name: params.row.TemplateName,
                  language: params.row.Language,
                  category: params.row.Category,

                  WABAAccountID: params.row.WABAAccountID
                    ? params.row.WABAAccountID
                    : "",

                  status: params.row.Status,
                  templateID: params.row.MetaTemplateID,

                  statusTime: moment
                    .utc(params.row.StatusTime)
                    .format("DD-MMM-yyyy HH:mm:ss"),

                  updatedTime: moment
                    .utc(params.row.UpdatedTime)
                    .format("DD-MMM-yyyy HH:mm:ss"),

                  isCallPermission: params.row.IsCallPermission,
                  parameterFormat: params.row.ParameterFormat,
                });

                updateComponentsValues((draft) =>
                  mapWATemplate(draft, params.row)
                );

                setAction("edit");
              }}
            >
              <Icon
                color="#7e7e7e"
                height="26"
                icon="ic:baseline-remove-red-eye"
              />
            </IconButton>
          </div>
        );
      },
    },
  ];

  const handleBack = () => {
    setAction(null);
    updateComponentsValues(() => initialComponentsValues);
    setTemplateDetail(initialTemplateDetail);
  };

  const handleClose = () => {
    handleBack();
    setIsWhatsappTemplateDialogShown(false);
  };

  // Format phone number. If it begins with "0" replace it with "62"
  const formatPhoneNumber = (number) => {
    if (number.charAt(0) === "0") {
      return (number = "62" + number.slice(1));
    } else {
      return number;
    }
  };

  const handleSubmit = () => {
    setIsSubmitting(true);

    // Collect all non-empty bodyVariable values dynamically
    const bodyVariables = Array.from({ length: 10 }, (_, i) => {
      if (templateDetail.parameterFormat === "positional") {
        const key = `bodyVariable${i + 1}`;
        return componentsValues[key];
      } else {
        const valueKey = `bodyVariable${i + 1}`;
        const nameKey = `bodyVariableName${i + 1}`;
        const exampleValue = componentsValues[valueKey];
        const paramName = componentsValues[nameKey];

        // Only include if both exist
        if (paramName && exampleValue) {
          return {
            param_name: paramName,
            example: exampleValue,
          };
        }

        return null;
      }
    }).filter(Boolean);

    let componentsArray = [
      {
        type: "BODY",
        text: componentsValues.bodyText,

        example: bodyVariables.length
          ? {
              // body_text: [bodyVariables],

              [templateDetail.parameterFormat === "positional"
                ? "body_text"
                : "body_text_named_params"]:
                templateDetail.parameterFormat === "positional"
                  ? [bodyVariables]
                  : bodyVariables,
            }
          : null,
      },
    ];

    if (componentsValues.header.mediaType !== "none") {
      // Put header as first element
      componentsArray.splice(0, 0, {
        type: "HEADER",
        format: componentsValues.header.mediaType.toUpperCase(),
        text: componentsValues.header.headerText,

        example: componentsValues.header.headerVariable
          ? {
              // header_text: [componentsValues.header.headerVariable],

              [templateDetail.parameterFormat === "positional"
                ? "header_text"
                : "header_text_named_params"]:
                templateDetail.parameterFormat === "positional"
                  ? [componentsValues.header.headerVariable]
                  : [
                      {
                        param_name: componentsValues.header.headerVariableName,
                        example: componentsValues.header.headerVariable,
                      },
                    ],
            }
          : null,
      });
    }

    if (componentsValues.footerText) {
      componentsArray.push({
        type: "FOOTER",
        text: componentsValues.footerText,
      });
    }

    if (componentsValues.button.length) {
      componentsArray.push({
        type: "BUTTONS",

        buttons: componentsValues.button.map((button) => {
          if (button.buttonType === "QUICK_REPLY") {
            return {
              type: button.buttonType,
              text: button.buttonPlaceholder,
            };
          } else if (button.buttonType === "URL") {
            return {
              type: button.buttonType,
              text: button.buttonPlaceholder,
              url: button.URLPhone,
            };
          } else {
            return {
              type: button.buttonType,
              text: button.buttonPlaceholder,
              phone_number: formatPhoneNumber(button.URLPhone),
            };
          }
        }),
      });
    }

    if (templateDetail.isCallPermission) {
      componentsArray.push({
        type: "call_permission_request",
      });
    }

    console.log("payload", {
      name: templateDetail.name,
      language: templateDetail.language,
      category: templateDetail.category,
      wabaaccountid: templateDetail.WABAAccountID,
      components: componentsArray,
      parameter_format: templateDetail.parameterFormat,
    });

    if (action === "edit") {
      editWATemplate({
        templateid: templateDetail.templateID,
        category: templateDetail.category,
        components: componentsArray,
        parameter_format: templateDetail.parameterFormat,
      })
        .then((response) => {
          if (response.status) {
            console.log("Response create template: ", response.data);

            toast.success("Template Submitted", {
              autoClose: 3000,
            });

            setIsSubmitting(false);
            handleBack();
          } else {
            console.log("error", response.error);
            toast.error(response.error, { autoClose: 3000 });
            setIsSubmitting(false);
          }
        })
        .catch((err) => {
          console.log("err", err);
          toast.error(err, { autoClose: 3000 });
          setIsSubmitting(false);
        });
    } else {
      createWATemplate({
        name: templateDetail.name,
        language: templateDetail.language,
        category: templateDetail.category,
        wabaaccountid: templateDetail.WABAAccountID,
        components: componentsArray,
        parameter_format: templateDetail.parameterFormat,
      })
        .then((response) => {
          if (response.status) {
            console.log("Response create template: ", response.data);
            toast.success("Template Submitted", {
              autoClose: 3000,
            });
            setIsSubmitting(false);
            handleBack();
          } else {
            console.log("error", response.error);
            toast.error(response.error, { autoClose: 3000 });
            setIsSubmitting(false);
          }
        })
        .catch((err) => {
          console.log("err", err);
          toast.error(err, { autoClose: 3000 });
          setIsSubmitting(false);
        });
    }
  };

  function fetchTemplateList(search) {
    setIsLoading(true);

    getWATemplateList("", search)
      .then((response) => {
        if (response.status) {
          process.env.NODE_ENV === "development" &&
            console.log("Response template list: ", response.data);

          setTemplateList(response.data);
          setIsLoading(false);
        } else {
          console.log("error", response.error);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        console.log("err", err);
        setIsLoading(false);
      });
  }

  return (
    <Dialog
      open={isOpen}
      className="dialog whatsapp-template"
      fullWidth
      maxWidth="xxl"
      scroll="paper"
      onClose={handleClose}
    >
      <div className="dialog-container">
        <div className="dialog-content">
          <IconButton className="close-button" onClick={handleClose}>
            <CloseOutlined />
          </IconButton>

          <div className="title">
            {action && (
              <IconButton onClick={handleBack}>
                <Icon
                  height="20"
                  icon="material-symbols:arrow-back-ios-new-rounded"
                />
              </IconButton>
            )}

            <h2>
              {action === "add"
                ? "Add New Template"
                : action === "edit"
                ? "Edit Template"
                : "Whatsapp Template"}
            </h2>
          </div>

          {action ? (
            <AddEditWhatsappTemplate
              action={action}
              templateDetail={templateDetail}
              setTemplateDetail={setTemplateDetail}
              componentsValues={componentsValues}
              updateComponentsValues={updateComponentsValues}
              handleSubmit={handleSubmit}
              isSubmitting={isSubmitting}
            />
          ) : (
            <div className="container">
              <div className="contact_search">
                <div className="search-container">
                  <SearchOutlined className="icon" />

                  <input
                    type="search"
                    className="search"
                    placeholder="Search..."
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onInput={(e) => {
                      if (e.target.value.length === 0) {
                        setSearchValue("");
                      }
                    }}
                    onKeyDown={(e) =>
                      e.key === "Enter" && fetchTemplateList(searchValue)
                    }
                  />
                </div>

                <button
                  className="add-new-template"
                  type="submit"
                  onClick={() => setAction("add")}
                >
                  <Icon height="20" icon="material-symbols:add-rounded" />
                  Add New Template
                </button>
              </div>

              <DataGrid
                autoPageSize
                className="table"
                columns={columns}
                disableColumnFilter
                disableColumnMenu
                disableSelectionOnClick
                loading={isLoading}
                rows={templateList}
                getRowId={(row) => row.ID}
              />
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default WhatsappTemplateDialog;
